import type { AIAction } from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';

const label = (index: SemanticModelIndex, id: string): string => {
  const object = index.getDimension(id) ?? index.getMeasure(id) ?? index.getHierarchy(id);
  return object ? (object.label ?? object.name) : id;
};

/** Descripcion legible y determinista de una accion para mostrarla antes de aplicarla. */
export function describeAction(action: AIAction, index: SemanticModelIndex): string {
  switch (action.type) {
    case 'addDimension':
      return `Agregar dimension ${label(index, action.payload.dimensionId)}${
        action.payload.timeGrain === undefined ? '' : ` (${action.payload.timeGrain})`
      }`;
    case 'removeDimension':
      return `Quitar dimension ${label(index, action.payload.dimensionId)}`;
    case 'addMeasure':
      return `Agregar medida ${label(index, action.payload.measureId)}`;
    case 'removeMeasure':
      return `Quitar medida ${label(index, action.payload.measureId)}`;
    case 'addFilter': {
      const { target, operator, values } = action.payload.filter;
      const id = target.kind === 'dimension' ? target.dimensionId : target.measureId;
      return `Filtrar ${label(index, id)} ${operator} ${values.map(String).join(', ')}`.trim();
    }
    case 'removeFilter':
      return `Quitar filtro ${action.payload.filterId}`;
    case 'updateFilter':
      return `Modificar filtro ${action.payload.filterId}`;
    case 'clearFilters':
      return 'Quitar todos los filtros';
    case 'sort':
      return `Ordenar por ${action.payload.by
        .map(
          ({ target, direction }) =>
            `${label(index, target.kind === 'dimension' ? target.dimensionId : target.measureId)} (${direction})`,
        )
        .join(', ')}`;
    case 'setLimit':
      return action.payload.limit === null
        ? 'Quitar el limite de filas'
        : `Limitar a ${String(action.payload.limit)} filas`;
    case 'setVisualization':
      return `Cambiar visualizacion a ${action.payload.type}`;
    case 'drillDown':
      return `Bajar un nivel en ${label(index, action.payload.hierarchyId)}`;
    case 'drillUp':
      return `Subir un nivel en ${label(index, action.payload.hierarchyId)}`;
    case 'createMeasure':
      return `Proponer medida ${action.payload.measure.label ?? action.payload.measure.name} (requiere confirmacion)`;
    case 'createDimension':
      return `Proponer dimension ${action.payload.dimension.label ?? action.payload.dimension.name} (requiere confirmacion)`;
    case 'createCalculatedColumn':
      return `Proponer columna ${action.payload.column.label ?? action.payload.column.name} (requiere confirmacion)`;
    case 'comparePeriods':
      return `Comparar periodos (${action.payload.comparison.kind})`;
    case 'resetAnalysis':
      return 'Reiniciar el analisis';
    case 'narrative':
      return 'Comentario del asistente';
    case 'updateGrouping':
      return 'Ajustar totales y subtotales';
    case 'resetSelections':
      return action.payload.dimensionId === undefined
        ? 'Limpiar todas las selecciones'
        : `Limpiar selecciones de ${label(index, action.payload.dimensionId)}`;
    case 'requestExplanation':
      return `Explicar (${action.payload.topic})`;
    case 'suggestVisualization':
      return `Sugerir visualizacion ${action.payload.type}`;
  }
}
